import React from 'react';
import {
  BsArchive,
  BsArrowLeftRight,
  BsBell,
  BsBuilding,
  BsEnvelope,
  BsFlag,
  BsFolder,
  BsGear,
  BsGlobe,
  BsPass,
  BsPeople,
  BsPeopleFill,
  BsPerson
} from 'react-icons/bs';
import { IoPaperPlaneOutline, IoPersonAddOutline } from 'react-icons/io5';

import { FeatureFlags } from '../components/Authorizer';
import PINSAIIcon from '../components/PINSAIIcon';
import TemplateBuilderItemPanel
  from '../components/RequirementTemplateBuilder/components/RequirementTemplateLeftBox/components/RequirementTemplateBuilderBodyLeft/components/TemplateBuilderItemPanel';
import TemplateBuilderPolicyPanel
  from '../components/RequirementTemplateBuilder/components/RequirementTemplateLeftBox/components/RequirementTemplateBuilderBodyLeft/components/TemplateBuilderPolicyPanel';
import TemplateBuilderDocumentPanel
  from '../components/RequirementTemplateBuilder/components/RequirementTemplateRightBox/components/RequirementTemplateBuilderBodyRight/components/TemplateBuilderDocumentPanel';
import TemplateBuilderLanguagePanel
  from '../components/RequirementTemplateBuilder/components/RequirementTemplateRightBox/components/RequirementTemplateBuilderBodyRight/components/TemplateBuilderLanguagePanel';
import TemplateBuilderQuestionPanel
  from '../components/RequirementTemplateBuilder/components/RequirementTemplateRightBox/components/RequirementTemplateBuilderBodyRight/components/TemplateBuilderQuestionPanel';
import TabHeaderPanel from '../pages/AdminPage/components/TabHeaderPanelSearch';
import CertificatesPanel from '../pages/AdminPage/panels/CertificatesPanel';
import CodesTable from '../pages/AdminPage/panels/CodesTable';
import CustomDomainPanel from '../pages/AdminPage/panels/CustomDomainPanel';
import CustomRolesPanel from '../pages/AdminPage/panels/CustomRolesPanel';
import DepartmentsTabPanel from '../pages/AdminPage/panels/DepartmentsTabPanel';
import DepartmentForm from '../pages/AdminPage/panels/DepartmentsTabPanel/components/DepartmentForm';
import EmailAutomationPanel from '../pages/AdminPage/panels/EmailAutomationPanel';
import EmailRulesPanel from '../pages/AdminPage/panels/EmailRulesPanel';
import Integrations from '../pages/AdminPage/panels/Integrations';
import UsersTabPanel from '../pages/AdminPage/panels/UsersTabPanel';
import UserForm from '../pages/AdminPage/panels/UsersTabPanel/components/UserForm';
import CreateNewFolderTab from '../pages/TemplatesListPage/components/CreateNewFolderTab';
import EmailTemplates from '../pages/TemplatesListPage/EmailTemplates';
import RequirementsTemplates from '../pages/TemplatesListPage/RequirementsTemplates';
import ArchivedTemplatesListPanel
  from '../pages/TemplatesListPage/RequirementsTemplates/panels/ArchivedTemplatesListPanel';
import DraftTemplatesListPanel from '../pages/TemplatesListPage/RequirementsTemplates/panels/DraftTemplatesListPanel';
import TabPanelHeader from '../pages/UserSettingsPage/components/TabPanelHeader';
import EmailTabPanel from '../pages/UserSettingsPage/panels/EmailTabPanel';
import ProfileTabPanel from '../pages/UserSettingsPage/panels/ProfileTabPanel';
import { globalRequirementTemplateBuilder, insuredRequirementTemplateBuilder } from '../redux/reducer';

import { analyticsIds } from './analyticsIds';
import { CypressIds } from './cypressIds';

export const TabMenuTypes = {
  adminMenuTabs: 'adminMenuTabs',
  userSettingsMenuTabs: 'userSettingsMenuTabs',
  templateFolderMenuTabs: 'templateFolderMenuTabs',
  templateTabs: 'templateTabs',
  createRequirementsTemplatesLeft: 'createRequirementsTemplatesLeft',
  createRequirementsTemplatesRight: 'createRequirementsTemplatesRight',
  createInsuredRequirementsLeft: 'createInsuredRequirementsLeft',
  createInsuredRequirementsRight: 'createInsuredRequirementsRight'
};

export const TabNames = {
  Account: 'account',
  Profile: 'profile',
  Notifications: 'notifications',
  EmailRules: 'email_rules',
  SubmissionRules: 'submission_rules',
  CustomDomain: 'custom_domain',
  CustomRoles: 'custom_roles',
  EmailSettings: 'email_settings',
  EmailAutomation: 'email_automation',
  Users: 'users',
  Departments: 'departments',
  Codes: 'codes',
  Integrations: 'integrations',
  Archived: 'archived',
  Drafts: 'drafts',
  CreateNewFolder: 'create_new_folder',
  Requirements: 'requirements',
  Emails: 'emails',
  Policies: 'policies',
  Items: 'items',
  Documents: 'documents',
  Language: 'language',
  Questions: 'questions'
};

const UserSettingsTabOptions = {
  [TabNames.Account]: {
    title: 'Account',
    description: 'Your account information',
    icon: BsGear,
    tabId: 'account'
  },
  [TabNames.Profile]: {
    title: 'Profile',
    description: 'Your profile preferences',
    icon: BsPerson,
    tabId: 'profile',
    main: ProfileTabPanel,
    dataAnalyticsId: analyticsIds.userSettings.profile.profileTab,
    dusk: analyticsIds.userSettings.profile.profileTab,
    header: () => <TabPanelHeader title="Profile" />
  },
  [TabNames.Notifications]: {
    title: 'Notifications',
    description: 'Your notification preferences',
    icon: BsBell,
    tabId: 'notifications'
  },
  [TabNames.EmailSettings]: {
    title: 'Email Settings',
    description: 'Your email preferences',
    icon: BsEnvelope,
    tabId: 'email',
    main: EmailTabPanel,
    dataAnalyticsId: analyticsIds.userSettings.emailSettings.emailSettingsTab,
    dusk: analyticsIds.userSettings.emailSettings.emailSettingsTab,
    header: () => <TabPanelHeader title="Email" />
  }
};

const AdminTabOptions = {
  [TabNames.Users]: {
    title: 'Users',
    description: 'Manage account users',
    icon: BsPeople,
    tabId: 'users',
    dataAnalyticsId: analyticsIds.adminSettingsPage.users.usersTab,
    dusk: analyticsIds.adminSettingsPage.users.usersTab,
    main: UsersTabPanel,
    header: () => (
      <TabHeaderPanel
        formComponent={UserForm}
        icon={IoPersonAddOutline}
        tabId="user"
        tabName="Users"
      />
    )
  },
  [TabNames.Departments]: {
    title: 'Departments',
    description: 'Manage account departments',
    icon: BsBuilding,
    tabId: 'departments',
    dataAnalyticsId: analyticsIds.adminSettingsPage.departments.departmentsTab,
    dusk: analyticsIds.adminSettingsPage.departments.departmentsTab,
    main: DepartmentsTabPanel,
    header: () => <TabHeaderPanel formComponent={DepartmentForm} tabId="department" tabName="Departments" />
  },
  [TabNames.Codes]: {
    title: 'Compliance Codes',
    description: 'Manage compliance codes',
    icon: BsFlag,
    tabId: 'codes',
    flag: FeatureFlags.Codes,
    dataAnalyticsId: analyticsIds.adminSettingsPage.compliance.complianceTab,
    dusk: analyticsIds.adminSettingsPage.compliance.complianceTab,
    main: CodesTable,
    header: () => null
  },
  [TabNames.SubmissionRules]: {
    title: 'Submission Rules',
    description: 'Set rules for certificate submissions.',
    icon: BsPass,
    tabId: TabNames.SubmissionRules,
    main: CertificatesPanel,
    header: () => null
  },
  [TabNames.EmailRules]: {
    title: 'Email Rules',
    description: 'Set recipients for outgoing emails',
    icon: BsEnvelope,
    tabId: TabNames.EmailRules,
    main: EmailRulesPanel,
    header: () => null
  },
  [TabNames.CustomRoles]: {
    title: 'Custom Roles',
    description: 'Create custom roles for users',
    icon: BsPeopleFill,
    tabId: TabNames.CustomRoles,
    flag: FeatureFlags.CustomRoles,
    main: CustomRolesPanel,
    header: () => null
  },
  [TabNames.EmailAutomation]: {
    title: 'Email Automation',
    description: 'Manage Email Automation',
    icon: IoPaperPlaneOutline,
    tabId: 'email-automation',
    flag: FeatureFlags.EmailAutomation,
    main: EmailAutomationPanel,
    header: () => null
  },
  [TabNames.CustomDomain]: {
    title: 'Custom Domain',
    description: 'Register your domain',
    icon: BsGlobe,
    tabId: TabNames.CustomDomain,
    main: CustomDomainPanel,
    header: () => null,
    onTabSelect: () => {}
  },
  [TabNames.Integrations]: {
    title: 'Integrations',
    description: 'Manage Integrations',
    icon: BsArrowLeftRight,
    tabId: TabNames.Integrations,
    main: Integrations,
    header: () => null
  }
};

const TemplateFoldersTabOptions = {
  [TabNames.Archived]: {
    id: 'archived',
    title: 'Archive',
    description: '',
    icon: BsArchive,
    tabId: 'archived',
    main: ArchivedTemplatesListPanel,
    header: () => null
  },
  [TabNames.Drafts]: {
    id: 'drafts',
    title: 'Drafts',
    description: '',
    icon: BsFolder,
    tabId: 'drafts',
    main: DraftTemplatesListPanel,
    header: () => null
  },
  [TabNames.CreateNewFolder]: {
    component: CreateNewFolderTab,
    tabId: 'create_new',
    main: () => null,
    header: () => null
  }
};

const CreateRequirementsTemplatesTabsLeftOptions = {
  [TabNames.Policies]: {
    tabId: 'requirement-templates',
    title: 'Policies',
    main: () => <TemplateBuilderPolicyPanel blueprint={globalRequirementTemplateBuilder} />
  },
  [TabNames.Items]: {
    tabId: 'item-templates',
    title: 'Items',
    main: () => <TemplateBuilderItemPanel blueprint={globalRequirementTemplateBuilder} />
  }
};

const CreateInsuredRequirementsLeftOptions = {
  [TabNames.Policies]: {
    tabId: 'requirement-templates',
    title: 'Policies',
    main: () => <TemplateBuilderPolicyPanel blueprint={insuredRequirementTemplateBuilder} />
  },
  [TabNames.Items]: {
    tabId: 'item-templates',
    title: 'Items',
    main: () => <TemplateBuilderItemPanel blueprint={insuredRequirementTemplateBuilder} />
  }
};

const CreateRequirementsTemplatesTabsRightOptions = {
  [TabNames.Documents]: {
    tabId: 'document-templates',
    title: 'Documents',
    main: () => <TemplateBuilderDocumentPanel blueprint={globalRequirementTemplateBuilder} />
  },
  [TabNames.Language]: {
    tabId: 'language-templates',
    title: 'Language',
    main: () => <TemplateBuilderLanguagePanel blueprint={globalRequirementTemplateBuilder} />
  },
  [TabNames.Questions]: {
    tabId: 'question-templates',
    title: 'AI Assistant',
    icon: PINSAIIcon,
    cypressId: CypressIds.questionMenuOption,
    main: () => <TemplateBuilderQuestionPanel blueprint={globalRequirementTemplateBuilder} />
  }
};

const CreateInsuredRequirementsRightOptions = {
  [TabNames.Documents]: {
    tabId: 'document-templates',
    title: 'Documents',
    main: () => <TemplateBuilderDocumentPanel blueprint={insuredRequirementTemplateBuilder} />
  },
  [TabNames.Language]: {
    tabId: 'language-templates',
    title: 'Language',
    main: () => <TemplateBuilderLanguagePanel blueprint={insuredRequirementTemplateBuilder} />
  },
  [TabNames.Questions]: {
    tabId: 'question-templates',
    title: 'AI Assistant',
    icon: PINSAIIcon,
    cypressId: CypressIds.questionMenuOption,
    main: () => <TemplateBuilderQuestionPanel blueprint={insuredRequirementTemplateBuilder} />
  }
};

const TemplateTabOptions = {
  [TabNames.Requirements]: {
    tabId: 'requirement-templates',
    title: 'Requirements',
    main: RequirementsTemplates
  },
  [TabNames.Emails]: {
    tabId: 'email-templates',
    title: 'Emails',
    main: EmailTemplates
  }
};

const GlobalTabTypeOptions = {
  [TabMenuTypes.userSettingsMenuTabs]: [
    // UserSettingsTabOptions[TabNames.Account],
    UserSettingsTabOptions[TabNames.Profile],
    // UserSettingsTabOptions[TabNames.Notifications],
    UserSettingsTabOptions[TabNames.EmailSettings]
  ],
  [TabMenuTypes.adminMenuTabs]: [
    AdminTabOptions[TabNames.Users],
    AdminTabOptions[TabNames.Departments],
    AdminTabOptions[TabNames.Codes],
    AdminTabOptions[TabNames.EmailRules],
    AdminTabOptions[TabNames.SubmissionRules],
    AdminTabOptions[TabNames.CustomDomain],
    AdminTabOptions[TabNames.CustomRoles],
    AdminTabOptions[TabNames.EmailAutomation],
    AdminTabOptions[TabNames.Integrations]
  ],
  [TabMenuTypes.templateFolderMenuTabs]: [
    TemplateFoldersTabOptions[TabNames.CreateNewFolder],
    TemplateFoldersTabOptions[TabNames.Drafts],
    TemplateFoldersTabOptions[TabNames.Archived]
  ],
  [TabMenuTypes.templateTabs]: [
    TemplateTabOptions[TabNames.Requirements]
    // TODO: Uncomment when Emails tab is ready
    // TemplateTabOptions[TabNames.Emails]
  ],
  [TabMenuTypes.createRequirementsTemplatesLeft]: [
    CreateRequirementsTemplatesTabsLeftOptions[TabNames.Policies]
    // TODO: Uncomment when Items tab is ready
    // CreateRequirementsTemplatesTabsLeftOptions[TabNames.Items]
  ],
  [TabMenuTypes.createRequirementsTemplatesRight]: [
    CreateRequirementsTemplatesTabsRightOptions[TabNames.Documents],
    CreateRequirementsTemplatesTabsRightOptions[TabNames.Language],
    CreateRequirementsTemplatesTabsRightOptions[TabNames.Questions]
  ],
  [TabMenuTypes.createInsuredRequirementsLeft]: [
    CreateInsuredRequirementsLeftOptions[TabNames.Policies],
    CreateInsuredRequirementsLeftOptions[TabNames.Items]
  ],
  [TabMenuTypes.createInsuredRequirementsRight]: [
    CreateInsuredRequirementsRightOptions[TabNames.Documents],
    CreateInsuredRequirementsRightOptions[TabNames.Language],
    CreateInsuredRequirementsRightOptions[TabNames.Questions]
  ]
};

const validateTabs = tabTypeOption => {
  if (!tabTypeOption || !TabMenuTypes[tabTypeOption]) {
    throw new Error(`Incorrect Status Type Provided, status type: ${tabTypeOption}`);
  }
};

export const getTabSelectOptions = tabsTypeOption => {
  validateTabs(tabsTypeOption);
  return GlobalTabTypeOptions[tabsTypeOption];
};
